/**
 * 프롬프트 dry-run 테스트 모달 (M14 / F-4) — admin 전용.
 *
 * 저장된 템플릿(template_id)을 임의 OCR 텍스트로 시뮬레이션한다.
 *   - 항상 rendered system/user 프롬프트(placeholder 치환 결과)를 반환.
 *   - ``call_llm`` 체크 시 실제 모델 호출 → extracted 결과까지 표시 (비용 발생).
 */

import { useState } from "react";
import { Loader2, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api/client";
import type { PromptTemplateOut } from "@/lib/api/prompts";

import { usePromptTest } from "./usePrompts";

interface Props {
  prompt: PromptTemplateOut;
  onClose: () => void;
}

export default function PromptTestModal({ prompt, onClose }: Props) {
  const { t } = useTranslation();
  const testMu = usePromptTest();

  const [ocrText, setOcrText] = useState("");
  const [agentId, setAgentId] = useState("");
  const [callLlm, setCallLlm] = useState(false);

  const canRun = ocrText.trim().length > 0 && !testMu.isPending;
  const apiError =
    testMu.error instanceof ApiError ? testMu.error.detail : (testMu.error?.message ?? null);
  const result = testMu.data;

  const handleRun = () => {
    if (!canRun) return;
    testMu.mutate({
      template_id: prompt.id,
      ocr_text: ocrText,
      agent_id: agentId.trim() || null,
      call_llm: callLlm,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 py-8"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-sm font-semibold text-slate-900">
            {t("admin.prompts.test.title", { version: prompt.version })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label={t("common.actions.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {/* 입력 */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
            <div>
              <label htmlFor="pt-agent" className="block text-xs font-medium text-slate-700">
                {t("admin.prompts.test.agentLabel")}
              </label>
              <input
                id="pt-agent"
                type="text"
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                maxLength={10}
                placeholder={t("admin.prompts.test.agentPlaceholder")}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <label className="flex items-end gap-1.5 pb-2 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={callLlm}
                onChange={(e) => setCallLlm(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              {t("admin.prompts.test.callLlm")}
            </label>
          </div>

          <div>
            <label htmlFor="pt-ocr" className="block text-xs font-medium text-slate-700">
              {t("admin.prompts.test.ocrLabel")}
            </label>
            <textarea
              id="pt-ocr"
              value={ocrText}
              onChange={(e) => setOcrText(e.target.value)}
              rows={6}
              placeholder={t("admin.prompts.test.ocrPlaceholder")}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-xs shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {apiError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {apiError}
            </div>
          )}

          {/* 결과 */}
          {result && (
            <div className="space-y-3">
              {result.agent_used && (
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  {t("admin.prompts.test.agentUsed", {
                    id: result.agent_used.id,
                    name: result.agent_used.official_name,
                    currency: result.agent_used.currency ?? "—",
                  })}
                </div>
              )}
              <ResultBlock
                title={t("admin.prompts.test.renderedSystem")}
                body={result.rendered_system_prompt}
              />
              <ResultBlock
                title={t("admin.prompts.test.renderedUser")}
                body={result.rendered_user_prompt}
              />
              {result.extracted && (
                <div>
                  <h3 className="text-xs font-medium text-slate-700">
                    {t("admin.prompts.test.extracted")}
                  </h3>
                  <pre className="mt-1 max-h-64 overflow-auto rounded-md border border-slate-200 bg-slate-900 px-3 py-2 font-mono text-[11px] text-slate-100">
                    {JSON.stringify(result.extracted, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 transition hover:bg-slate-100"
          >
            {t("common.actions.close")}
          </button>
          <button
            type="button"
            onClick={handleRun}
            disabled={!canRun}
            className={
              canRun
                ? "inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-blue-700"
                : "inline-flex cursor-not-allowed items-center gap-1.5 rounded-md bg-slate-300 px-3 py-1.5 text-xs font-medium text-white"
            }
          >
            {testMu.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
            {t("admin.prompts.test.run")}
          </button>
        </footer>
      </div>
    </div>
  );
}

function ResultBlock({ title, body }: { title: string; body: string }) {
  return (
    <div>
      <h3 className="text-xs font-medium text-slate-700">{title}</h3>
      <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded-md border border-slate-200 bg-white px-3 py-2 font-mono text-[11px] text-slate-700">
        {body}
      </pre>
    </div>
  );
}
